import { useEffect, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { InputField } from "@/components/common/InputField";
import { getOrganizationIpAllowlist, updateOrganizationIpAllowlist } from "@/lib/api";
import { INPUT_STYLE, focusRingOff, focusRingOn } from "@/lib/form-styles";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

const ERROR_RING = "0 0 0 1px var(--danger-solid)";

function parseLines(text: string): string[] {
	return text
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
}

/** backend は単一 IP を /32・/128 の CIDR に正規化して返すため、その形も追加済みとみなす。 */
function isCallerIpListed(lines: string[], callerIp: string): boolean {
	const singleHost = `${callerIp}${callerIp.includes(":") ? "/128" : "/32"}`;
	return lines.some((line) => line === callerIp || line === singleHost);
}

/** 組織が変わったら作り直す (key で指定)。保存中の応答が別組織のフォームに入らないようにする。 */
function IpAllowlistForm({ organizationId }: { organizationId: string }) {
	const [text, setText] = useState("");
	const [callerIp, setCallerIp] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		let cancelled = false;
		getOrganizationIpAllowlist(organizationId)
			.then((data) => {
				if (cancelled) return;
				setText(data.cidrs.join("\n"));
				setCallerIp(data.callerIp);
			})
			.catch((err: Error) => {
				if (!cancelled) setLoadError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	const callerIpListed = callerIp !== null && isCallerIpListed(parseLines(text), callerIp);

	function changeText(next: string) {
		setText(next);
		setSaved(false);
	}

	function addCallerIp() {
		if (callerIp === null || callerIpListed) return;
		changeText(text.trim().length === 0 ? callerIp : `${text.replace(/\n+$/, "")}\n${callerIp}`);
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSaveError(null);
		setSaved(false);
		setSaving(true);
		try {
			const updated = await updateOrganizationIpAllowlist(organizationId, parseLines(text));
			setText(updated.cidrs.join("\n"));
			setCallerIp(updated.callerIp);
			setSaved(true);
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setSaving(false);
		}
	}

	return loadError ? (
		<p
			data-testid="ip-allowlist-load-error"
			style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}
		>
			{loadError}
		</p>
	) : loading ? null : (
		<form
			onSubmit={handleSubmit}
			noValidate
			data-testid="ip-allowlist-form"
			style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 560 }}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					fontSize: "var(--text-ui)",
					color: "var(--fg-2)",
				}}
			>
				<span>
					現在の接続元 IP:{" "}
					<span
						data-testid="ip-allowlist-caller-ip"
						className="select-text"
						style={{ fontFamily: "var(--font-mono)", color: "var(--fg-1)" }}
					>
						{callerIp ?? "取得できませんでした"}
					</span>
				</span>
				{callerIp !== null && (
					<ConfirmButton
						type="button"
						variant="outline"
						size="sm"
						label="リストに追加"
						loading={false}
						succeeded={callerIpListed}
						disabled={callerIpListed}
						onClick={addCallerIp}
						data-testid="ip-allowlist-add-caller-ip"
					/>
				)}
			</div>
			<InputField
				label="許可する接続元 - 1 行に 1 つ、IP アドレスか、CIDR 表記の範囲 (末尾の /24 などで範囲を指定) を入力します。空にして保存すると制限を解除します。"
				error={saveError ?? undefined}
			>
				<textarea
					value={text}
					onChange={(e) => changeText(e.target.value)}
					placeholder={
						"例:\n198.51.100.7\n203.0.113.0/24 (203.0.113.0〜203.0.113.255 の範囲)\n2001:db8::/32 (IPv6 の範囲)"
					}
					spellCheck={false}
					data-testid="ip-allowlist-textarea"
					style={{
						...INPUT_STYLE,
						display: "block",
						height: 150,
						padding: "8px 10px",
						fontFamily: "var(--font-mono)",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-body)",
						resize: "vertical",
						...(saveError !== null ? { boxShadow: ERROR_RING } : {}),
					}}
					onFocus={(e) => focusRingOn(e.currentTarget)}
					onBlur={(e) => {
						if (saveError !== null) {
							e.currentTarget.style.boxShadow = ERROR_RING;
						} else {
							focusRingOff(e.currentTarget);
						}
					}}
				/>
			</InputField>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
				{saved && (
					<span
						data-testid="ip-allowlist-saved"
						style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}
					>
						保存しました。
					</span>
				)}
				<ConfirmButton
					type="submit"
					label="保存"
					loading={saving}
					data-testid="ip-allowlist-save"
				/>
			</div>
		</form>
	);
}

export function IpAllowlistTab() {
	const organizationId = useCurrentUserStore((s) => s.organizationId);
	const organizationName = useCurrentUserStore((s) => s.organizationName);

	return (
		<div style={{ padding: 32, height: "100%", overflowY: "auto" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					height: 40,
					flexShrink: 0,
					marginBottom: 8,
				}}
			>
				<h2
					style={{
						fontSize: "var(--text-heading)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
					}}
				>
					IP 制限
				</h2>
			</div>
			<p
				data-testid="ip-allowlist-organization"
				style={{
					margin: "0 0 24px",
					fontSize: "var(--text-ui)",
					color: "var(--fg-2)",
				}}
			>
				対象の組織:{" "}
				<span style={{ color: "var(--fg-1)" }}>{organizationName ?? organizationId}</span>
			</p>

			<IpAllowlistForm key={organizationId} organizationId={organizationId} />
		</div>
	);
}
